import type { InstantlyIdentityExclusionReference } from '@/store/features/prospects.feature';
import type {
  InstantlyLeadStatus,
  SearchFilters,
} from '@/types/search-filters';
import { buildCleanlistExclusionRequest } from '@/utils/cleanlist-exclusions.util';
import { getActiveCrmExclusions } from '@/utils/crm-exclusions.util';
import { isWellFormedCrmClause } from '@/utils/crm-section-clauses.util';
import { FilterCompileError } from '@/utils/filter-compile-error.util';
import { BRAND } from '@/lib/brand';

/** The Instantly lead statuses, as the In Instantly control and chip name them. */
export const INSTANTLY_STATUS_LABELS: Record<InstantlyLeadStatus, string> = {
  replied: 'Replied',
  interested: 'Interested',
  not_interested: 'Not interested',
  bounced: 'Bounced',
  unsubscribed: 'Unsubscribed',
};

/** Unsupported saved clauses must stay visible instead of becoming a wider query. */
export function validatePeopleExclusions(filters: SearchFilters): void {
  for (const crm of ['salesforce', 'hubspot'] as const) {
    const clauses = filters.crmFilterSections?.[crm] ?? [];
    if (!Array.isArray(clauses) || !clauses.every(isWellFormedCrmClause)) {
      throw new FilterCompileError(
        'crmFilterSections',
        'CRM exclusions',
        'A selected CRM filter is invalid. Remove or correct it before searching'
      );
    }
    if (clauses.some((c) => c.polarity === 'include')) {
      throw new FilterCompileError(
        'crmFilterSections',
        'CRM inclusion',
        'This saved inclusion filter is not supported. Remove it before searching'
      );
    }
  }
  if (
    filters.excludeInCleanlist === 'list' &&
    !buildCleanlistExclusionRequest(filters)
  ) {
    throw new FilterCompileError(
      'excludeInCleanlistListIds',
      `${BRAND.name} exclusions`,
      'Choose at least one list or turn this exclusion off'
    );
  }
  const instantlyMode = filters.excludeInInstantly ?? 'off';
  if (!['off', 'any_campaign', 'statuses'].includes(instantlyMode)) {
    throw new FilterCompileError(
      'excludeInInstantly',
      'In Instantly',
      'This saved In Instantly setting is not supported. Turn it off or choose it again'
    );
  }
  const blocklist = filters.excludeInInstantlyBlocklist as unknown;
  if (blocklist != null && typeof blocklist !== 'boolean') {
    throw new FilterCompileError(
      'excludeInInstantlyBlocklist',
      'In Instantly',
      'This saved Instantly blocklist setting is not supported. Turn it off or choose it again'
    );
  }
  const statuses = filters.excludeInInstantlyStatuses ?? [];
  if (
    instantlyMode === 'statuses' &&
    statuses.some(
      (status) =>
        !Object.prototype.hasOwnProperty.call(INSTANTLY_STATUS_LABELS, status)
    )
  ) {
    throw new FilterCompileError(
      'excludeInInstantlyStatuses',
      'In Instantly',
      'A saved Instantly lead status is not supported. Choose the statuses again'
    );
  }
  if (instantlyMode === 'statuses' && statuses.length === 0) {
    throw new FilterCompileError(
      'excludeInInstantlyStatuses',
      'In Instantly',
      'Choose at least one lead status or turn this exclusion off'
    );
  }
}

/** The ``instantly`` identity exclusion reference for the search request, or
 *  null when the control is off. Statuses ride only with the statuses mode, so
 *  a stale pick left behind by another mode never reaches the server. */
export function buildInstantlyExclusionReference(
  filters: SearchFilters
): InstantlyIdentityExclusionReference | null {
  const mode = filters.excludeInInstantly ?? 'off';
  const blocklist = filters.excludeInInstantlyBlocklist === true;
  if (mode === 'off' && !blocklist) return null;
  return {
    mode,
    ...(mode === 'statuses'
      ? { statuses: [...new Set(filters.excludeInInstantlyStatuses ?? [])] }
      : {}),
    ...(blocklist ? { blocklist: true } : {}),
  };
}

export function hasDynamicPeopleExclusions(filters: SearchFilters): boolean {
  return (
    getActiveCrmExclusions(filters).length > 0 ||
    Object.values(filters.crmFilterSections ?? {}).some(
      (clauses) => !Array.isArray(clauses) || clauses.length > 0
    ) ||
    (filters.excludeInCleanlist != null &&
      filters.excludeInCleanlist !== 'off') ||
    buildInstantlyExclusionReference(filters) !== null
  );
}

export function hasPeopleExclusions(filters: SearchFilters): boolean {
  return (
    hasDynamicPeopleExclusions(filters) ||
    filters.excludeProfileUrls.length > 0 ||
    filters.excludeCompanyDomains.length > 0 ||
    filters.jobTitlesExclude.length > 0
  );
}

export function requireCompleteCrmExclusions(status: {
  partial: boolean;
  truncated: boolean;
  stale: boolean;
  erroredKeys: string[];
}): void {
  if (
    status.partial ||
    status.truncated ||
    status.stale ||
    status.erroredKeys.length > 0
  ) {
    throw new FilterCompileError(
      'crmFilterSections',
      'CRM exclusions',
      'The selected filters could not be fully resolved. Refresh your CRM data or remove the affected filter'
    );
  }
}

const FIELD_REFUSAL_CODE = 'invalid_field_condition';

/** The server's words for a refused CRM ``field:`` clause, from an RTK Query
 *  error: ``detail.message``, or the text after the code in a string detail
 *  (``invalid_field_condition: <message>``). Null for any other error, which
 *  keeps the generic CRM message. */
export function crmFieldRefusal(error: unknown): string | null {
  const detail = (error as { data?: { detail?: unknown } } | null)?.data
    ?.detail;
  const message =
    typeof detail === 'string'
      ? detail.startsWith(`${FIELD_REFUSAL_CODE}:`)
        ? detail.slice(FIELD_REFUSAL_CODE.length + 1)
        : null
      : (detail as { message?: unknown } | null)?.message;
  return typeof message === 'string' && message.trim() ? message.trim() : null;
}
